<template>
  <BfwComponent :isReady="isReady" :title="title" :showTitle="showTitle" :editable="editable">
    <v-form ref="form" v-model="valid" lazy-validation :isReady="isReady">
      <v-row>
        <v-col>
          <div class="d-flex align-center">
            <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details></v-text-field>
            <v-btn v-if="$access.permissions.includes('Administer Betaworkflow')" @click="openExportDialog" color="primary" class="ml-4">
              Export Contacts
            </v-btn>
          </div>
          <v-data-table :headers="headers" :items="clients" :items-per-page="1000" :search="search" :custom-filter="filter" hide-default-footer>

            <template v-slot:item.Contacts="{item}">
              <div v-for="contact in item.Contacts.Contact" v-bind:key="contact.UUID">
                <a @click="selectContact(contact, item)">{{ contact.Name }}</a>
              </div>
            </template>

          </v-data-table>
        </v-col>
      </v-row>

     <ClientPopup :selected-client="selectedClient" :selected-contact="selectedContact"/>
    </v-form>

    <v-dialog v-model="exportDialog" max-width="400" @input="onExportDialogToggle">
      <v-card>
        <v-card-title class="headline">Export Contacts</v-card-title>
        <v-card-text>
          <v-alert type="warning" dense text>This export can take up to 30 minutes to complete.</v-alert>
          <p>Select the date range of contact creation dates to include.</p>
          <p v-if="dateRange.length === 2"><strong>Selected range:</strong> {{ sortedDateRange[0] }} to {{ sortedDateRange[1] }}</p>
          <v-date-picker v-model="dateRange" range no-title scrollable full-width :disabled="jobId !== null"></v-date-picker>

          <div v-if="jobId" class="mt-4">
            <div v-if="jobStatus === 'error'" class="red--text">
              Export failed: {{ jobError }}
            </div>
            <div v-else-if="jobStatus === 'done'">
              Export ready.
            </div>
            <div v-else>
              <p>{{ jobStatus === 'running' ? 'Exporting' : 'Starting' }}<span v-if="jobTotal">&nbsp;({{ jobProcessed }} / {{ jobTotal }} clients)</span>...</p>
              <v-progress-linear :indeterminate="!jobTotal" :value="jobTotal ? (jobProcessed / jobTotal * 100) : 0"></v-progress-linear>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn color="primary" text @click="closeExportDialog">
            {{ jobStatus === 'done' ? 'Close' : 'Cancel' }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="jobStatus === 'done'" color="primary" text @click="downloadExport">
            Download
          </v-btn>
          <v-btn v-else color="primary" text :disabled="dateRange.length !== 2 || jobId !== null" @click="startExport">
            Start Export
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </BfwComponent>
</template>

<script>
import BfwComponent from "../BwfComponent"
import ClientPopup from "@/components/clients/ClientPopup.vue"

export default {
  name: "ClientsContactList",
  permissions: ["Read Client", "Administer Betaworkflow", ...ClientPopup.permissions],
  components: {
    ClientPopup,
    BfwComponent,
  },
  computed: {
    sortedDateRange() {
      return [...this.dateRange].sort()
    },
  },
  methods: {
    processResponse(response) {
      this.clients = this.$ensureArray(response.Clients.Client)
      for (let i = 0; i < this.clients.length; i++) {
        if (!this.clients[i].Contacts) {
          this.clients[i].Contacts = {Contact: []}
        }
        this.clients[i].Contacts.Contact = this.$ensureArray(this.clients[i].Contacts.Contact)
      }
      this.isReady = true
    },
    selectContact(contact, client) {
      this.selectedContact = contact
      this.selectedClient = client
    },
    filter: function (value, searchStr, item) {
      const searchRegExp = new RegExp(searchStr, "i")
      if (item.Address && item.Address.search(searchRegExp) !== -1) {
        return true
      }
      if (item.Name && item.Name.search(searchRegExp) !== -1) {
        return true
      }
      if (item.Phone && item.Phone.search(searchRegExp) !== -1) {
        return true
      } //todo regex this
      for (let i = 0; i < item.Contacts.Contact.length; i++) {
        const contact = item.Contacts.Contact[i]
        if (contact.Name && contact.Name.search(searchRegExp) !== -1) {
          return true
        }
        if (contact.Email && contact.Email.search(searchRegExp) !== -1) {
          return true
        }
        if (contact.Mobile && contact.Mobile.search(searchRegExp) !== -1) {
          return true
        } //todo regex this
        if (contact.Phone && contact.Phone.search(searchRegExp) !== -1) {
          return true
        } //todo regex this
      }
      return false
    },
    openExportDialog() {
      this.dateRange = []
      this.jobId = null
      this.jobStatus = null
      this.jobError = null
      this.jobTotal = 0
      this.jobProcessed = 0
      this.exportDialog = true
    },
    onExportDialogToggle(isOpen) {
      if (!isOpen) {
        this.closeExportDialog()
      }
    },
    startExport() {
      const [fromDate, toDate] = this.sortedDateRange
      this.$BwfApi.post("contacts/export", {fromDate: fromDate, toDate: toDate}).then(response => {
        this.jobId = response.jobId
        this.jobStatus = "pending"
        this.pollTimer = setInterval(() => this.pollExportJob(), 4000)
      })
    },
    pollExportJob() {
      this.$BwfApi.get("background-jobs/" + this.jobId).then(job => {
        this.jobStatus = job.status
        this.jobTotal = job.total_steps
        this.jobProcessed = job.processed_steps
        if (job.status === "done") {
          clearInterval(this.pollTimer)
          this.pollTimer = null
          this.downloadExport()
        } else if (job.status === "error") {
          clearInterval(this.pollTimer)
          this.pollTimer = null
          this.jobError = job.error
        }
      })
    },
    downloadExport() {
      this.$BwfApi.get("background-jobs/" + this.jobId + "/download", {}, {responseType: "blob"}).then(response => {
        const objectUrl = window.URL.createObjectURL(response)
        const anchor = document.createElement("a")
        document.body.appendChild(anchor)
        anchor.href = objectUrl
        anchor.download = "contacts-export.csv"
        anchor.click()
      })
    },
    closeExportDialog() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.exportDialog = false
    },
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }
  },
  mounted() {
    this.$WfmApi.get("client.api/list").then(response => this.processResponse(response))
  },
  data() {
    return {
      title: "Contacts List",
      showTitle: false,
      editable: false,
      isReady: false,
      valid: true,
      search: "",
      settings: {},
      clients: [],
      selectedContact: {},
      selectedClient: {},
      headers: [
        {text: "Client", value: "Name"},
        {text: "Contact", value: "Contacts"},
      ],
      exportDialog: false,
      dateRange: [],
      jobId: null,
      jobStatus: null,
      jobError: null,
      jobTotal: 0,
      jobProcessed: 0,
      pollTimer: null,
    }
  },
}

</script>

<style scoped>

</style>